import * as Sentry from "@sentry/react";

// Lifecycle Phase 1 → Phase 5 item 14: web DSN now provided (separate Sentry
// project "pac-web" from the backend project). Still a no-op if unset.
const dsn = process.env.REACT_APP_SENTRY_DSN;
const environment = process.env.REACT_APP_SENTRY_ENVIRONMENT || "development";
const isProduction = environment === "production";

// R9/16.3 — same email/phone scrub as the backend's Sentry before_send hook
// (server.py::_scrub_pii_text) so no player/coach PII ever reaches Sentry
// from a thrown error message, even if it slipped into the text itself.
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const PHONE_RE = /(\+?\d[\d\-\s().]{7,}\d)/g;
function scrubText(value) {
  if (typeof value !== "string") return value;
  return value.replace(EMAIL_RE, "[scrubbed-email]").replace(PHONE_RE, "[scrubbed-phone]");
}

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: isProduction ? 0.2 : 1.0,
    replaysSessionSampleRate: isProduction ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: [/^\//, process.env.REACT_APP_BACKEND_URL].filter(Boolean),
    sendDefaultPii: false,
    beforeSend(event) {
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
      }
      if (event.request?.headers) {
        delete event.request.headers.authorization;
        delete event.request.headers.cookie;
      }
      if (event.message) event.message = scrubText(event.message);
      for (const exc of event.exception?.values || []) {
        if (exc.value) exc.value = scrubText(exc.value);
      }
      return event;
    },
  });
  // B6: visiting any page with ?sentry_test=1 sends one tagged web test event (no user data).
  if (new URLSearchParams(window.location.search).get("sentry_test") === "1") {
    const id = Sentry.captureMessage(`stageb_sentry_test web env=${environment} release=${process.env.REACT_APP_SENTRY_RELEASE}`, "warning");
    console.info("sentry_test event", id);
  }
}

// Set once at login/logout (see App.js) — an opaque account_id tag, never a
// name/email, so incidents in Sentry can still be filtered per account.
export function setSentryUserTags({ accountId, appVersion } = {}) {
  if (!dsn) return;
  Sentry.setTag("account_id", accountId || undefined);
  Sentry.setTag("app_version", appVersion || undefined);
  Sentry.setTag("platform", "web");
}

export { Sentry };
